// leadQualificationCompleteness.js

import {
  getChecklistTemplateItemLabel,
  normalizeChecklistTemplateItems,
} from 'utilities/ChecklistTemplateItems';
import { resolveSchemaFieldValue } from 'views/utils/fieldValueResolver';

/**
 * Prefer a field named `qualified`, else the first checklist with template items.
 *
 * @param {unknown} fields
 * @returns {object|null}
 */
export function resolveLeadQualificationChecklistField(fields) {
  const list = Array.isArray(fields) ? fields : [];
  const checklistFields = list.filter((field) => (
    String(field?.type || '').trim().toLowerCase() === 'checklist'
    && normalizeChecklistTemplateItems(field?.checklistItems).length > 0
  ));

  if (!checklistFields.length) return null;

  const preferred = checklistFields.find((field) => (
    String(field?.name || field?.key || '').trim().toLowerCase() === 'qualified'
  ));
  return preferred || checklistFields[0];
}

/**
 * @param {unknown} value
 * @returns {{ totalItems: number, completedItems: number, completedKeys: Set<string>, isComplete: boolean }}
 */
export function readChecklistSummary(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {
      totalItems: 0,
      completedItems: 0,
      completedKeys: new Set(),
      isComplete: false,
    };
  }

  const totalItems = Number(value.totalItems) || 0;
  const completedItems = Number(value.completedItems) || 0;
  const completedKeys = new Set(
    (Array.isArray(value.completedKeys) ? value.completedKeys : [])
      .map((entry) => String(entry || '').trim())
      .filter(Boolean),
  );

  return {
    totalItems,
    completedItems,
    completedKeys,
    isComplete: totalItems > 0 && completedItems >= totalItems,
  };
}

/**
 * @param {object} params
 * @param {object[]} params.nodes
 * @param {object|null|undefined} params.field
 * @returns {object|null}
 */
export function buildLeadQualificationCompleteness({ nodes = [], field = null } = {}) {
  const schemaField = field && typeof field === 'object' ? field : null;
  const templateItems = normalizeChecklistTemplateItems(schemaField?.checklistItems);
  if (!schemaField || !templateItems.length) return null;

  const fieldName = String(schemaField.name || schemaField.key || '').trim();
  if (!fieldName) return null;

  const reportableNodes = (Array.isArray(nodes) ? nodes : []).filter((node) => (
    node
    && typeof node === 'object'
    && node.isCatalogPlaceholder !== true
  ));

  let completeCount = 0;
  let completenessSum = 0;
  let withAnyProgress = 0;

  const keyStats = templateItems.map((item) => ({
    key: item.key,
    label: getChecklistTemplateItemLabel(item) || item.key,
    doneCount: 0,
  }));
  const keyIndex = new Map(keyStats.map((entry, index) => [entry.key, index]));

  reportableNodes.forEach((node) => {
    const raw = resolveSchemaFieldValue({
      item: node,
      fieldDef: schemaField,
      typeKey: 'LEAD',
    });
    const summary = readChecklistSummary(raw);
    if (summary.totalItems > 0 || summary.completedKeys.size > 0) {
      withAnyProgress += 1;
    }

    const denominator = summary.totalItems > 0
      ? summary.totalItems
      : templateItems.length;
    const numerator = summary.totalItems > 0
      ? Math.min(summary.completedItems, denominator)
      : templateItems.filter((item) => summary.completedKeys.has(item.key)).length;

    completenessSum += denominator > 0 ? numerator / denominator : 0;
    if (denominator > 0 && numerator >= denominator) {
      completeCount += 1;
    }

    templateItems.forEach((item) => {
      if (!summary.completedKeys.has(item.key)) return;
      const index = keyIndex.get(item.key);
      if (index == null) return;
      keyStats[index].doneCount += 1;
    });
  });

  const leadCount = reportableNodes.length;
  const avgCompleteness = leadCount > 0 ? completenessSum / leadCount : 0;
  const percentComplete = leadCount > 0 ? completeCount / leadCount : 0;

  return {
    fieldName,
    fieldLabel: String(schemaField.label || fieldName).trim() || fieldName,
    leadCount,
    completeCount,
    withAnyProgress,
    avgCompleteness,
    percentComplete,
    items: keyStats.map((entry) => ({
      ...entry,
      percentDone: leadCount > 0 ? entry.doneCount / leadCount : 0,
    })),
  };
}

/**
 * @param {number|null|undefined} ratio
 * @returns {string}
 */
export function formatCompletenessPercent(ratio) {
  if (!Number.isFinite(ratio)) return '—';
  return `${Math.round(ratio * 100)}%`;
}
